import React, { useCallback } from "react";
import { Box } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockIcon from "@mui/icons-material/Lock";
import { useTranslation } from "react-i18next";
import { useModal } from "../../components/Modal";
import ConfirmActionModalContent from "../../components/Modal/ConfirmAction";
import { AddTenantModalContent } from "../../components/Modal/AddTenant";
import { CustomerKeyConfirmationModalContent } from "../../components/Modal/CustomerKeyConfirmation";
import { ViewTenantModalContent } from "../../components/Modal/ViewTenant";
import { EditTenantModalContent } from "../../components/Modal/EditTenant";
import { tenantsService, Tenant } from "../../services/tenantsService";
import { WarningIcon } from "../../components/Icons";
import { colors } from "../../theme/colors";

const MODAL_IDS = {
  ADD_TENANT: "add-tenant-modal",
  CUSTOMER_KEY: "customer-key-modal",
  VIEW_TENANT: "view-tenant-modal",
  EDIT_TENANT: "edit-tenant-modal",
  ACTIVATE: "activate-tenant-modal",
  SUSPEND: "suspend-tenant-modal",
  REACTIVATE: "reactivate-tenant-modal",
  RESTORE: "restore-tenant-modal",
} as const;

type ShowToast = (message: string, severity?: "success" | "error" | "info") => void;

export function useTenantModals(fetchTenants: () => void, showToast: ShowToast) {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();

  const openEditTenantModal = useCallback(
    (tenant: Tenant) => {
      openModal({
        id: MODAL_IDS.EDIT_TENANT,
        showCloseButton: true,
        closeOnBackdropClick: false,
        maxWidth: false,
        width: 560,
        containerPadding: "0",
        content: (
          <EditTenantModalContent
            tenant={tenant}
            onClose={() => closeModal(MODAL_IDS.EDIT_TENANT)}
            onSuccess={() => {
              closeModal(MODAL_IDS.EDIT_TENANT);
              showToast(t("tenantManagement.toasts.updated"));
              fetchTenants();
            }}
          />
        ),
      });
    },
    [openModal, closeModal, fetchTenants, showToast, t],
  );

  const openAddTenantModal = useCallback(() => {
    openModal({
      id: MODAL_IDS.ADD_TENANT,
      showCloseButton: true,
      closeOnBackdropClick: false,
      maxWidth: false,
      width: 560,
      containerPadding: "0",
      content: (
        <AddTenantModalContent
          onClose={() => closeModal(MODAL_IDS.ADD_TENANT)}
          onSuccess={(created) => {
            closeModal(MODAL_IDS.ADD_TENANT);
            openModal({
              id: MODAL_IDS.CUSTOMER_KEY,
              showCloseButton: false,
              closeOnBackdropClick: false,
              maxWidth: false,
              width: 480,
              containerPadding: "0",
              content: (
                <CustomerKeyConfirmationModalContent
                  clinicName={created.clinicName}
                  customerKey={created.customerKey}
                  onDone={() => {
                    closeModal(MODAL_IDS.CUSTOMER_KEY);
                    fetchTenants();
                  }}
                />
              ),
            });
          }}
        />
      ),
    });
  }, [openModal, closeModal, fetchTenants]);

  const openViewTenantModal = useCallback(
    (tenant: Tenant) => {
      openModal({
        id: MODAL_IDS.VIEW_TENANT,
        showCloseButton: true,
        maxWidth: false,
        width: 600,
        containerPadding: "0",
        content: (
          <ViewTenantModalContent
            tenant={tenant}
            onClose={() => closeModal(MODAL_IDS.VIEW_TENANT)}
            onEdit={() => {
              closeModal(MODAL_IDS.VIEW_TENANT);
              openEditTenantModal(tenant);
            }}
          />
        ),
      });
    },
    [openModal, closeModal, openEditTenantModal],
  );

  const openActivateModal = useCallback(
    (tenant: Tenant) => {
      openModal({
        id: MODAL_IDS.ACTIVATE,
        maxWidth: 500,
        containerPadding: "48px 56px",
        content: (
          <ConfirmActionModalContent
            iconNode={
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: colors.status.activeBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircleOutlineIcon
                  sx={{ color: colors.status.activeText, fontSize: 32 }}
                />
              </Box>
            }
            title={t("tenantManagement.activate.title")}
            message={t("tenantManagement.activate.message", { name: tenant.clinicName })}
            confirmText={t("tenantManagement.activate.confirm")}
            confirmButtonColor="success"
            onConfirm={async () => {
              await tenantsService.activateTenant(tenant.id);
              closeModal(MODAL_IDS.ACTIVATE);
              showToast(t("tenantManagement.toasts.activated", { name: tenant.clinicName }));
              fetchTenants();
            }}
            onCancel={() => closeModal(MODAL_IDS.ACTIVATE)}
          />
        ),
      });
    },
    [openModal, closeModal, fetchTenants, showToast, t],
  );

  const openSuspendModal = useCallback(
    (tenant: Tenant) => {
      openModal({
        id: MODAL_IDS.SUSPEND,
        maxWidth: 500,
        containerPadding: "48px 56px",
        content: (
          <ConfirmActionModalContent
            iconNode={
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: colors.amber.light,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <WarningIcon width={32} height={32} fill={colors.text.white} />
              </Box>
            }
            title={t("tenantManagement.suspend.title")}
            message={t("tenantManagement.suspend.message", { name: tenant.clinicName })}
            confirmText={t("tenantManagement.suspend.confirm")}
            confirmButtonColor="warning"
            onConfirm={async () => {
              await tenantsService.suspendTenant(tenant.id);
              closeModal(MODAL_IDS.SUSPEND);
              showToast(t("tenantManagement.toasts.suspended", { name: tenant.clinicName }));
              fetchTenants();
            }}
            onCancel={() => closeModal(MODAL_IDS.SUSPEND)}
          />
        ),
      });
    },
    [openModal, closeModal, fetchTenants, showToast, t],
  );

  const openReactivateModal = useCallback(
    (tenant: Tenant) => {
      openModal({
        id: MODAL_IDS.REACTIVATE,
        maxWidth: 500,
        containerPadding: "48px 56px",
        content: (
          <ConfirmActionModalContent
            iconNode={
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: colors.status.activeBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircleOutlineIcon
                  sx={{ color: colors.status.activeText, fontSize: 32 }}
                />
              </Box>
            }
            title={t("tenantManagement.reactivate.title")}
            message={t("tenantManagement.reactivate.message", { name: tenant.clinicName })}
            confirmText={t("tenantManagement.reactivate.confirm")}
            confirmButtonColor="success"
            onConfirm={async () => {
              await tenantsService.reactivateTenant(tenant.id);
              closeModal(MODAL_IDS.REACTIVATE);
              showToast(t("tenantManagement.toasts.reactivated", { name: tenant.clinicName }));
              fetchTenants();
            }}
            onCancel={() => closeModal(MODAL_IDS.REACTIVATE)}
          />
        ),
      });
    },
    [openModal, closeModal, fetchTenants, showToast, t],
  );

  const openRestoreModal = useCallback(
    (tenant: Tenant) => {
      openModal({
        id: MODAL_IDS.RESTORE,
        maxWidth: 500,
        containerPadding: "48px 56px",
        content: (
          <ConfirmActionModalContent
            iconNode={
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: colors.background.lightBlue,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LockIcon sx={{ color: colors.primary.main, fontSize: 28 }} />
              </Box>
            }
            title={t("tenantManagement.restore.title")}
            message={t("tenantManagement.restore.message", { name: tenant.clinicName })}
            confirmText={t("tenantManagement.restore.confirm")}
            confirmButtonColor="primary"
            onConfirm={async () => {
              await tenantsService.restoreTenant(tenant.id);
              closeModal(MODAL_IDS.RESTORE);
              showToast(t("tenantManagement.toasts.restored", { name: tenant.clinicName }));
              fetchTenants();
            }}
            onCancel={() => closeModal(MODAL_IDS.RESTORE)}
          />
        ),
      });
    },
    [openModal, closeModal, fetchTenants, showToast, t],
  );

  return {
    openAddTenantModal,
    openViewTenantModal,
    openEditTenantModal,
    openActivateModal,
    openSuspendModal,
    openReactivateModal,
    openRestoreModal,
  };
}
